import { BaseGamePadMobileDrawer } from 'bog-arcade-core-ui/drawers';

import { gamePadMobileResource } from '../../../core/resources';
import { landScapeConfig } from '../../../core/applicationConfig';
import { GameLayers } from '../../../core/enums';
import { gamePadService } from '../../../services/helperServices/GamePadService';
import {
  handleSpinCountsActiveChange,
  handleMobileQuickBetsClick,
  handleQuickBetsIndexChange,
  decrementAutoSpinCount,
  incrementAutoSpinCount,
  incrementNextQuickBet,
  decrementNextQuickBet,
  handleSpinClick,
  handleSwitchBall,
  switchButtonSubscriber,
  spinButtonSubscriber,
  autoSpinSubscriber,
  betAmountSubscriber,
  handleAutoSpinClick,
} from '../events';
import { resourceHelper } from '../../../core/resourceHelper';
import { sharedSourceNames } from '../../../core/sourceNames';
import { translationKeys } from '../../../core/translation/translation';
import { autoSpinCountTextStyle } from '../../../core/textStyles';
import { autoSpinHelper } from '../../../services/helperServices/AutoSpinService';
import { animationDrawer } from '../../../core/AnimationDrawer';
import { balanceLastWinDrawer } from '../../balanceLastWin/BalanceLastWinDrawer';

export class GamePadLandscapeDrawer extends BaseGamePadMobileDrawer {
  constructor() {
    super();
    this.container = null;
  }

  async loadResource() {
    await super.loadResource(gamePadMobileResource, this.createLoader()).then(({ res }) => {
      this.resources = res;
    });
  }

  setupLayers(layers) {
    this.container = layers[GameLayers.gamePad];
  }

  setupContainer() {
    this.setPosition(this.container, { y: 1100 });
  }

  setLastWinAndBalance(isBonus = false) {
    const container = this.createContainer('LastWinAndBalance');
    const content = balanceLastWinDrawer.getContent({
      isBonus,
      sizes: { w: 200 },
      position: { x: 20, y: 20 },
      fontSize: 25,
    });
    this.setPosition(container, { y: -120 });
    container.addChild(content);
    this.container.addChild(container);
  }

  setSpinIcon() {
    const [content, button] = this.getSpinButton({ scale: 2 });
    const [autoSpinContent, countTexture, infinityTexture] = this.getAutoSpinButton({
      scale: 2,
      textStyle: { ...autoSpinCountTextStyle, fontSize: 40 },
    });

    const animateSpin = () => animationDrawer.spin(button);
    this.setPosition(autoSpinContent, { x: landScapeConfig.width / 1.1, y: -landScapeConfig.height / 2 });
    this.setPosition(content, { x: landScapeConfig.width / 1.1, y: -landScapeConfig.height / 2 });
    this.addSubscriber(gamePadService.subscribeSpinState(state => spinButtonSubscriber(state, button), true));
    this.registerClick(content, (event) => {
      if (gamePadService.spinState) {
        animateSpin();
      }
      handleSpinClick(event);
    });

    autoSpinContent.visible = false;
    infinityTexture.visible = false;

    this.addSubscriber(gamePadService.autoSpinButtonSubscribe((state) => {
      if (!gamePadService.currentSpinCount) {
        autoSpinContent.visible = state;
        content.visible = !state;
      } else {
        autoSpinContent.visible = true;
        content.visible = false;
      }
    }));

    this.addSubscriber(gamePadService.subscribeCurrentCountChange((state) => {
      if (state === Infinity) {
        countTexture.visible = false;
        infinityTexture.visible = true;
      } else {
        countTexture.visible = true;
        infinityTexture.visible = false;
        countTexture.text = state;
      }
    }));

    this.registerClick(autoSpinContent, () => autoSpinHelper.end());

    this.container.addChild(content, autoSpinContent);
  }

  setBetAmountButton() {
    const [content, onToggle, buttonContainer] = this.getCircleButton({
      icon: this.sourceNames.coinsIcon,
      scale: 1.5,
    });

    const [buttonContent, renderOption, { prev, next }] = this.getButtonsContent({
      nextIcon: this.sourceNames.plusIcon,
      prevIcon: this.sourceNames.minusIcon,
      showCircle: false,
      scale: 1.4,
      title: 'Bet Amount',
      onNext: incrementNextQuickBet,
      onPrev: decrementNextQuickBet,
    });

    buttonContainer.addChild(buttonContent);
    this.setPosition(buttonContainer,
      { x: content.width - buttonContent.width - 80, y: content.height - buttonContent.height });
    this.setupButtonMode(content);
    this.registerClick(content, handleMobileQuickBetsClick);
    this.addSubscriber(gamePadService.betAmountSubscribe(state => {
      betAmountSubscriber(state, content);
    }));
    this.addSubscriber(gamePadService.subscribeQuickBetsActiveState(onToggle));
    this.addSubscriber(gamePadService.subscribeQuickBet(renderOption));
    this.addSubscriber(gamePadService.subscribeQuickBetsIndex(index => {
      handleQuickBetsIndexChange(index, { prev, next });
    }));
    this.setPosition(content, { x: landScapeConfig.width / 1.1, y: -landScapeConfig.height / 2 + 250 });
    this.container.addChild(content);
  }

  setAutoSpinButton() {
    const [content, onToggle, buttonContainer] = this.getCircleButton({
      icon: this.sourceNames.autoSpinPlayIcon,
      scale: 1.5,
      title: translationKeys.play,
    });

    const [buttonContent, renderOption, { prevCircle, nextCircle }] = this.getButtonsContent({
      nextIcon: this.sourceNames.rightArrowIcon,
      prevIcon: this.sourceNames.leftArrowIcon,
      showCircle: true,
      scale: 1.4,
      title: translationKeys.spinCount,
      onNext: incrementAutoSpinCount,
      onPrev: decrementAutoSpinCount,
    });

    buttonContainer.addChild(buttonContent);
    this.setPosition(buttonContainer,
      { x: content.width - buttonContent.width - 80, y: content.height - buttonContent.height });
    this.setupButtonMode(content);
    this.addSubscriber(gamePadService.subscribeSpinCountsActiveState(onToggle));
    this.addSubscriber(gamePadService.subscribeAutoSpinCount(renderOption));
    this.addSubscriber(gamePadService.subscribeSpinCountIndex(index => {
      handleSpinCountsActiveChange(index, { prev: prevCircle, next: nextCircle });
    }));

    this.addSubscriber(gamePadService.autoSpinSubscribe(state => {
      autoSpinSubscriber(state, content);
    }));

    this.registerClick(content, handleAutoSpinClick);
    this.setPosition(content, { x: landScapeConfig.width / 1.1, y: -landScapeConfig.height / 2 - 250 });
    this.container.addChild(content);
  }

  setSwitchButton() {
    const switchTexture = this.createSprite(resourceHelper.get(sharedSourceNames.switchIcon));
    switchTexture.anchor.set(.5);
    switchTexture.scale.set(.7);
    this.setupButtonMode(switchTexture);
    this.registerClick(switchTexture, (event) => handleSwitchBall(event));
    this.addSubscriber(gamePadService.switchButtonSubscribe(state => {
      switchButtonSubscriber(state, switchTexture);
    }));
    this.addSubscriber(gamePadService.subscribeQuickBetsActiveState((state) => {
      switchTexture.visible = !state;
    }));

    this.setPosition(switchTexture, { x: 1480, y: -290 });
    this.container.addChild(switchTexture);
  }

  execute() {
    this.setupContainer();
    this.setSpinIcon();
    this.setAutoSpinButton();
    this.setBetAmountButton();
    this.setSwitchButton();
    this.setLastWinAndBalance();
  }
}
